import { changePasswordSchema, type ChangePasswordInput, type LoginResponse } from '@ruby/shared';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { AppForm, Field, SubmitButton, useAppForm } from '@/components/form/form';
import { PasswordRules } from '@/components/form/password-rules';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, Card } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { loadBootstrap, logout } from '@/lib/auth';
import { useSession } from '@/lib/session';
import { Brand } from '../shell/brand';
import { SplashScreen } from '../shell/status-pages';
import { TwoStepCard } from './two-step';

export function ChangePasswordPage() {
  const status = useSession((s) => s.status);
  const forced = useSession((s) => s.boot?.user.mustChangePassword);
  const navigate = useNavigate();
  const form = useAppForm(changePasswordSchema, { currentPassword: '', newPassword: '', confirmPassword: '' });
  const newPassword = form.watch('newPassword');

  if (status === 'loading') return <SplashScreen />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <div className="mb-6">
        <Brand />
      </div>
      <Card className="w-full max-w-md p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-primary-soft text-primary">
            <KeyRound className="size-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Change password</h1>
            <p className="text-[13px] text-fg-muted">Choose a strong password you do not use elsewhere.</p>
          </div>
        </div>
        {forced && (
          <Alert tone="warning" className="mb-4" title="Password change required">
            Your administrator requires you to set a new password before continuing.
          </Alert>
        )}
        <AppForm
          form={form}
          guardUnsaved={false}
          successMessage="Password changed. Other devices have been signed out."
          className="flex flex-col gap-4"
          onSubmit={async (v: ChangePasswordInput) => {
            const res = await api<LoginResponse>('/auth/change-password', { method: 'POST', body: v });
            useSession.getState().setToken(res.accessToken);
            await loadBootstrap();
            await navigate({ to: '/', replace: true });
          }}
        >
          <Field name="currentPassword" label="Current password" required>
            {(p) => <Input {...p} {...form.register('currentPassword')} type="password" autoComplete="current-password" autoFocus />}
          </Field>
          <Field name="newPassword" label="New password" required>
            {(p) => <Input {...p} {...form.register('newPassword')} type="password" autoComplete="new-password" />}
          </Field>
          <PasswordRules value={newPassword} />
          <Field name="confirmPassword" label="Confirm new password" required>
            {(p) => <Input {...p} {...form.register('confirmPassword')} type="password" autoComplete="new-password" />}
          </Field>
          <div className="mt-2 flex justify-between gap-2">
            {forced ? (
              <Button variant="ghost" onClick={() => void logout()}>
                Sign out
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => history.back()}>
                Cancel
              </Button>
            )}
            <SubmitButton>Update password</SubmitButton>
          </div>
        </AppForm>
      </Card>
      {!forced && <div className="mt-4 w-full max-w-md"><TwoStepCard /></div>}
    </div>
  );
}
